import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import PreviewPanel from "./tools-panel/preview-panel";
import CodeEditor from "./tools-panel/code-editor";
import DatabaseViewer from "./tools-panel/database-viewer";
import DevToolsDrawer from "./tools-panel/dev-tools-drawer";
import BrowserControls from "./tools-panel/preview-panel/BrowserControls";
import Monitor from "./Monitor";
import ProjectFilesViewer from "../ProjectFilesViewer";
import { injectCustomScrollbarStyles } from "./tools-panel/styles";
import { useSettingsModal } from "../../contexts/SettingsModalContext";

// Inject styles
injectCustomScrollbarStyles();

const ToolsPanel = forwardRef(
  (
    {
      selectedToolTab,
      setSelectedToolTab,
      logs,
      terminalTabs,
      activeTerminalTab,
      setActiveTerminalTab,
      setTerminalTabs,
      agentStatus,
      logRef,
      terminalRef,
      splitWidth,
      messages = [],
      sandboxHost,
      sandboxStatus,
      workspaceId,
      workspace,
      setInput,
      handleProgrammaticSubmit,
      hideHeader = false,
    },
    ref,
  ) => {
    // Main state - intelligently choose initial view mode
    const getInitialViewMode = () => {
      // If workspace is already available, default to preview only for 'vibe', otherwise monitor
      if (workspace?.project_mode === "vibe") {
        return "preview";
      } else if (workspace?.project_mode) {
        return "monitor";
      }
      // Return null if workspace not loaded to indicate loading state
      return null;
    };

    const [viewMode, setViewMode] = useState(getInitialViewMode);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedBottomTab, setSelectedBottomTab] = useState("terminal");
    
    // Use selectedToolTab prop when provided (mobile overlay), otherwise use internal viewMode
    // Only use selectedToolTab if hideHeader is true (mobile overlay mode)
    const currentViewMode = hideHeader ? selectedToolTab : viewMode;
    const [isDesktopView, setIsDesktopView] = useState(() => {
      // Default to mobile view for Expo templates, desktop for others
      if (workspace?.template_slug === 'expo-template') {
        return false; // Mobile view for Expo
      }
      return !hideHeader; // Original logic for other templates
    });
    const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
    const containerRef = useRef(null);
    const previewPanelRef = useRef(null);
    const codeEditorRef = useRef(null);

    // Update view mode when workspace changes (for Expo templates)
    useEffect(() => {
      if (workspace?.template_slug === 'expo-template' && !hideHeader) {
        setIsDesktopView(false); // Switch to mobile view for Expo
      }
    }, [workspace?.template_slug, hideHeader]);
    const databaseViewerRef = useRef(null);
    const projectFilesRef = useRef(null);
    const monitorRef = useRef(null);
    const { openProjectSettings } = useSettingsModal();

    // Memoized terminal content to prevent unnecessary re-renders
    const activeTerminalContent = React.useMemo(() => {
      const activeTab = terminalTabs.find(
        (tab) => tab.id === activeTerminalTab,
      );
      if (!activeTab) return "";
      return activeTab.content || "";
    }, [terminalTabs, activeTerminalTab]);

    const handleBottomTabChange = (tab) => {
      setSelectedBottomTab(tab);
      // Don't sync with parent in mobile overlay mode to prevent view changes
      if (!hideHeader) {
        setSelectedToolTab(tab); // Only sync with parent for desktop mode
      }
    };

    const handleRefresh = useCallback(() => {
      // Call refresh on the appropriate component based on view mode
      if (!viewMode) return; // Don't refresh if we're still loading

      if (viewMode === "preview" && previewPanelRef.current?.refresh) {
        previewPanelRef.current.refresh();
      } else if (viewMode === "monitor" && monitorRef.current?.refresh) {
        monitorRef.current.refresh();
      } else if (viewMode === "code" && codeEditorRef.current?.refresh) {
        codeEditorRef.current.refresh();
      } else if (viewMode === "database" && databaseViewerRef.current?.refresh) {
        databaseViewerRef.current.refresh();
      } else if (viewMode === "project_files" && projectFilesRef.current?.refresh) {
        projectFilesRef.current.refresh();
      }
    }, [viewMode]);

    // Expose handleRefresh method to parent component
    useImperativeHandle(
      ref,
      () => ({
        handleRefresh,
      }),
      [handleRefresh],
    );

    const handleOpenInNewWindow = useCallback(() => {
      if (sandboxHost) {
        window.open(`https://${sandboxHost}`, "_blank");
      }
    }, [sandboxHost]);

    // Initialize split tab state based on selectedToolTab prop
    useEffect(() => {
      if (selectedToolTab === "terminal" || selectedToolTab === "logs") {
        setSelectedBottomTab(selectedToolTab);
      }
    }, [selectedToolTab]);

    // Set view mode when workspace becomes available
    useEffect(() => {
      // Only update if viewMode is null (loading) and workspace data is now available
      if (viewMode === null && workspace?.project_mode) {
        const targetMode =
          workspace.project_mode === "vibe" ? "preview" : "monitor";
        setViewMode(targetMode);
      }
    }, [workspace?.project_mode, viewMode]);

    return (
      <div
        className="k-tools-panel flex flex-col h-full overflow-hidden relative"
        ref={containerRef}
      >
        {/* Top Section - takes remaining space */}
        <div className="flex-1 h-full bg-slate-100 dark:bg-neutral-900 overflow-hidden rounded-t-2xl">
          <div className="h-full flex flex-col bg-slate-100 dark:bg-neutral-900 overflow-hidden">
            {/* Show browser controls once we know the view mode */}
            {currentViewMode !== null && !hideHeader && (
              <BrowserControls
                sandboxHost={sandboxHost}
                onRefresh={handleRefresh}
                onOpenInNewWindow={handleOpenInNewWindow}
                viewMode={currentViewMode}
                onViewModeChange={setViewMode}
                isDesktopView={isDesktopView}
                onViewChange={setIsDesktopView}
                viewDropdownOpen={viewDropdownOpen}
                setViewDropdownOpen={setViewDropdownOpen}
                workspace={workspace}
                onOpenProjectSettings={() =>
                  workspaceId ? openProjectSettings(workspaceId) : null
                }
                hideHeader={hideHeader}
              />
            )}


            {/* Content area */}
            <div className="flex-1 overflow-auto">
              {currentViewMode === null ? (
                // Loading state - show a skeleton or spinner while workspace loads
                <div className="flex items-center justify-center h-full">
                  <div className="text-ui-text-secondary">
                    <div className="animate-pulse">
                      <div className="h-8 w-48 bg-neutral-200 dark:bg-neutral-700 rounded mb-4 mx-auto"></div>
                      <div className="h-4 w-32 bg-neutral-200 dark:bg-neutral-700 rounded mx-auto"></div>
                    </div>
                  </div>
                </div>
              ) : currentViewMode === "preview" ? (
                <PreviewPanel
                  ref={previewPanelRef}
                  sandboxHost={sandboxHost}
                  sandboxStatus={sandboxStatus}
                  setInput={setInput}
                  handleProgrammaticSubmit={handleProgrammaticSubmit}
                  viewMode={currentViewMode}
                  onViewModeChange={setViewMode}
                  isDesktopView={isDesktopView}
                  setIsDesktopView={setIsDesktopView}
                  viewDropdownOpen={viewDropdownOpen}
                  setViewDropdownOpen={setViewDropdownOpen}
                  agentStatus={agentStatus}
                  showControls={false} // Don't show controls again
                  workspace={workspace}
                />
              ) : currentViewMode === "monitor" ? (
                <Monitor
                  ref={monitorRef}
                  messages={messages}
                  agentStatus={agentStatus}
                />
              ) : currentViewMode === "code" ? (
                <CodeEditor
                  ref={codeEditorRef}
                  workspaceId={workspaceId}
                  workspace={workspace}
                />
              ) : currentViewMode === "database" ? (
                <DatabaseViewer
                  ref={databaseViewerRef}
                  workspaceId={workspaceId}
                />
              ) : currentViewMode === "project_files" ? (
                <ProjectFilesViewer ref={projectFilesRef} workspaceId={workspaceId} />
              ) : null}
            </div>
          </div>
        </div>

        {/* Transparent overlay when hideHeader and dev tools is open */}
        {hideHeader && drawerOpen && (
          <div
            className="fixed inset-0 bg-transparent z-10"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* Bottom Drawer */}
        <DevToolsDrawer
          isOpen={drawerOpen}
          onToggle={() => setDrawerOpen(!drawerOpen)}
          selectedTab={selectedBottomTab}
          onTabChange={handleBottomTabChange}
          logs={logs}
          logRef={logRef}
          terminalTabs={terminalTabs}
          activeTerminalTab={activeTerminalTab}
          setActiveTerminalTab={setActiveTerminalTab}
          setTerminalTabs={setTerminalTabs}
          agentStatus={agentStatus}
          terminalRef={terminalRef}
        />
      </div>
    );
  },
);

export default ToolsPanel;
